import { ArrowLeftIcon, Spinner } from 'evergreen-ui';
import React, { AnchorHTMLAttributes } from 'react';
import styled from 'styled-components';
import { keyframes } from 'styled-components';
import { AnchorComponent, LinkComponent, LinkComponentTypes } from './A';

export const clickHandler = (f?: Function) => (e: any) => {
    try {
        let elementClicked = e?.target?.localName === 'button' ? 'Button' : 'Link';
        if (e?.target?.getAttribute('title')) {
            // @ts-expect-error Untyped global
            window.analytics.track(
                `${elementClicked} clicked: ${e?.target?.getAttribute('title')}`,
                { integrations: { Intercom: false } }
            );
        } else if (e?.target?.textContent) {
            // @ts-expect-error Untyped global
            window.analytics.track(`${elementClicked} clicked: ${e?.target?.textContent}`, {
                integrations: { Intercom: false },
            });
        }
        if (typeof f === 'function') {
            return f(e);
        }
    } catch (e) {}
};

const shakeAnimation = keyframes`
    10%, 90% { transform: translate3d(-1px, 0, 0); }
    20%, 80% { transform: translate3d(2px, 0, 0); }
    30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
    40%, 60% { transform: translate3d(4px, 0, 0); }
`;

const COLORS = {
    blue: 'var(--cademy-blue)',
    green: 'var(--green)',
    red: 'var(--red)',
    yellow: 'var(--yellow)',
    black: 'var(--black)',
    white: 'var(--white)',
    none: 'var(--white)',
};

type ButtonComponentTypes = {
    children?: React.ReactNode;
    onClick?: Function;
    className?: string;
    desc?: string;
    type?: 'button' | 'submit';
    isLoading?: boolean;
    disabled?: boolean;
    rounded?: boolean;
    small?: boolean;
} & React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;
export type ButtonRef = HTMLButtonElement;

const ButtonComponent = React.forwardRef<ButtonRef, ButtonComponentTypes>(
    (
        { children, onClick, type = 'button', desc, disabled, isLoading, rounded, small, ...props },
        ref
    ) => (
        <button
            onClick={clickHandler(onClick)}
            type={type}
            {...props}
            disabled={disabled || isLoading}
            title={desc}
            ref={ref}
        >
            {isLoading && <Spinner size={16} style={{ marginRight: '5px' }} />}
            {children}
        </button>
    )
);

type BackButtonTypes = LinkComponentTypes & {
    children?: React.ReactNode;
    to: string;
    onClick?: Function;
    className?: string;
};

export type BackButtonRef = HTMLAnchorElement;

const BackButtonComponent = ({ children, to, ...props }: BackButtonTypes) => (
    <LinkComponent to={to} {...props}>
        <ArrowLeftIcon marginRight="10px" />
        {children || 'Back'}
    </LinkComponent>
);

const BackButtonAncherComponent = ({
    children,
    href,
    ...props
}: AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a href={href} {...props}>
        <ArrowLeftIcon marginRight="10px" />
        {children || 'Back'}
    </a>
);

type ButtonStyleTypes = {
    type?: 'button' | 'submit';
    margin?: string;
    appearance?: 'minimal' | 'primary' | 'outlined' | 'grey' | 'inverted';
    color?: keyof typeof COLORS;
    size?: 'sm' | 'md' | 'lg';
    rounded?: boolean;
    small?: boolean;
    disabled?: boolean;
    children?: React.ReactNode;
};

export const StyledButton = styled.button.attrs<ButtonStyleTypes>(({ margin }) => ({
    margin,
}))<ButtonStyleTypes>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    letter-spacing: -0.05px;
    font: unset;
    color: var(--text);
    border-radius: ${(props) => (props.rounded ? '25px;' : '4px;')};
    padding: ${(props) => (props.size === 'sm' ? '5px 5px;' : '0px 20px;')};
    height: ${(props) => (props.size === 'sm' ? 'auto;' : '40px;')};
    line-height: ${(props) => (props.size === 'sm' ? 'auto;' : '40px;')};
    font-weight: 500;
    font-size: 14px;
    border: unset;
    background-color: var(--white);
    margin: ${({ margin }) => margin};

    ${(props) => {
        switch (props.appearance) {
            case 'outlined':
                return `
                border: 1px solid ${props.color ? COLORS[props.color] : COLORS.black};
                color: ${props.color ? COLORS[props.color] : COLORS.black};
                `;
            case 'grey':
                return `
                    background-color: var(--bg-2);
                    border: 1px solid var(--border-muted);
                    `;
            case 'primary':
                return `
                    border: 1px solid transparent; 
                    background-color: ${props.color ? COLORS[props.color] : COLORS.blue};
                    color: ${props.color && ['yellow'].includes(props.color) ? `var(--black)` : `var(--white)`};
                    `;
            default:
                return `border: 1px solid transparent;`;
        }
    }}

    &:hover {
        cursor: pointer;

        ${(props) => {
            switch (props.appearance) {
                case 'outlined':
                    return `
                        border: 1px solid ${props.color ? COLORS[props.color] : COLORS.blue};
                        background-color: ${props.color ? COLORS[props.color] : COLORS.blue};
                        color: var(--white);
                        fill: var(--white);
                        `;
                case 'grey':
                    return `
                        background-color: var(--bg-1);
                        border: 1px solid var(--border);
                    `;
                case 'primary':
                    return `
                        opacity: 0.95;
                        box-shadow: inset 0px 0 10px 2px rgb(0 0 0 / 15%);
                        transition: all 0.2s;
                    `;
                default:
                    return `
                        border: 1px solid transparent; 
                        background-color: var(--bg-2);
                        `;
            }
        }}
    }

    ${(props) =>
        props.disabled &&
        `
        outline: unset;
        background-color: var(--bg-2);
        color: var(--text-muted);

        &:hover{
            box-shadow: none;
            cursor: not-allowed;
        }
        `}

    &:disabled {
        outline: unset;
        background-color: var(--bg-2);
        color: var(--text-muted);
    }

    &:disabled:hover {
        box-shadow: none;
    }

    &:focus {
        outline: unset;
        border: 1px solid var(--border);
    }

    &.shake {
        animation: ${shakeAnimation} 1s normal;
    }
`;

export const Button = StyledButton.withComponent(ButtonComponent);

export const IconButton = styled(Button)`
    padding: ${(props) => (props.small ? '5px' : '10px')};
    height: auto;
`;

export const BackButton = Button.withComponent(BackButtonComponent);
export const BackButtonAnchor = Button.withComponent(BackButtonAncherComponent);

//HTML links that looks like buttons
export const ButtonLink = Button.withComponent(LinkComponent);
export const ButtonAnchor = Button.withComponent(AnchorComponent);
